import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { PlaybookNode } from "@/hooks/usePlaybooks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

interface TreeNodeCardProps {
  node: PlaybookNode;
  x: number;
  y: number;
  isHighlighted?: boolean;
  isOrphan?: boolean;
  onEdit: (node: PlaybookNode) => void;
  onDelete: (id: string) => void;
  onHover: (nodeId: string | null) => void;
}

export function TreeNodeCard({
  node,
  x,
  y,
  isHighlighted,
  isOrphan,
  onEdit,
  onDelete,
  onHover,
}: TreeNodeCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: node.id,
    data: { node, x, y },
  });

  const style = {
    position: "absolute" as const,
    left: `${x}px`,
    top: `${y}px`,
    width: "280px",
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 50 : 1,
  };

  const isStart = node.node_id === "start";

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        "cursor-default transition-all duration-200",
        isStart && "border-primary border-2",
        node.is_end && "border-muted-foreground/50 border-dashed",
        isHighlighted && "ring-2 ring-primary shadow-lg",
        isDragging && "opacity-80 shadow-2xl",
        isOrphan && "border-warning border-dashed opacity-70"
      )}
      onMouseEnter={() => onHover(node.node_id)}
      onMouseLeave={() => onHover(null)}
    >
      <CardHeader className="pb-2 pt-3 px-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 -ml-1 hover:bg-muted rounded"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            <Badge
              variant={isStart ? "default" : "secondary"}
              className="text-xs"
            >
              {node.node_id}
            </Badge>
            {node.is_end && (
              <Badge variant="outline" className="text-xs">
                End: {node.end_type}
              </Badge>
            )}
            {isOrphan && (
              <Badge variant="destructive" className="text-xs">
                Orphan
              </Badge>
            )}
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onEdit(node)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onDelete(node.id)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <CardTitle className="text-sm font-medium leading-tight mt-1">
          {node.question.length > 60
            ? node.question.substring(0, 60) + "..."
            : node.question}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3">
        {node.options && node.options.length > 0 && (
          <div className="space-y-1 mt-1">
            {node.options.slice(0, 3).map((opt, i) => (
              <div key={i} className="text-xs flex items-center gap-1 text-muted-foreground">
                <span>→</span>
                <span className="truncate flex-1">{opt.label}</span>
                {opt.nextNode && (
                  <Badge variant="outline" className="text-[10px] px-1">
                    {opt.nextNode}
                  </Badge>
                )}
              </div>
            ))}
            {node.options.length > 3 && (
              <div className="text-xs text-muted-foreground">
                +{node.options.length - 3} more
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
